// Catálogo das conquistas cadastráveis no web (docs/prototipo/conquistas-cadastro.html, aprovado em 02/10/2026).
// Espelho das constantes de server/src/contratos/conquistas.ts e conquistas-acervo.ts: o web só importa TIPOS do
// contrato (nada de zod no bundle), então os valores moram aqui e `catalogo.test.ts` trava a igualdade com o servidor.
// O SERVIDOR é a autoridade (valida tudo de novo); isto só desenha a tela e a ajuda dos campos.
import type {
  CorNeon,
  CriterioConquista,
  EstiloMedalha,
  GrupoAcervo,
  IconeConquista,
  TipoConquista,
} from "@grindset/server/contratos";

// ── Acervo (§2): a ordem é a do painel (força, cardio, rotina, marcos, com a turma, casa e equipe) ───────────

export const GRUPOS_ACERVO: readonly GrupoAcervo[] = ["Força", "Cardio", "Rotina", "Marcos", "Com a turma", "Casa e equipe"];

export const ACERVO: readonly { chave: IconeConquista; rotulo: string; grupo: GrupoAcervo }[] = [
  { chave: "halter", rotulo: "Halter", grupo: "Força" },
  { chave: "barra", rotulo: "Barra com anilhas", grupo: "Força" },
  { chave: "anilha", rotulo: "Anilha", grupo: "Força" },
  { chave: "kettlebell", rotulo: "Kettlebell", grupo: "Força" },
  { chave: "supino", rotulo: "Supino", grupo: "Força" },
  { chave: "corda", rotulo: "Corda de pular", grupo: "Força" },
  { chave: "tenis", rotulo: "Tênis de corrida", grupo: "Cardio" },
  { chave: "pulso", rotulo: "Batimento", grupo: "Cardio" },
  { chave: "bike", rotulo: "Bike", grupo: "Cardio" },
  { chave: "cronometro", rotulo: "Cronômetro", grupo: "Cardio" },
  { chave: "raio", rotulo: "Raio", grupo: "Cardio" },
  { chave: "sol", rotulo: "Nascer do sol", grupo: "Rotina" },
  { chave: "lua", rotulo: "Lua", grupo: "Rotina" },
  { chave: "calendario", rotulo: "Calendário", grupo: "Rotina" },
  { chave: "relogio", rotulo: "Relógio", grupo: "Rotina" },
  { chave: "garrafa", rotulo: "Garrafa de água", grupo: "Rotina" },
  { chave: "pico", rotulo: "Pico", grupo: "Marcos" },
  { chave: "chegada", rotulo: "Bandeira de chegada", grupo: "Marcos" },
  { chave: "alvo", rotulo: "Alvo", grupo: "Marcos" },
  { chave: "trofeu", rotulo: "Troféu", grupo: "Marcos" },
  { chave: "coroa", rotulo: "Coroa", grupo: "Marcos" },
  { chave: "estrela", rotulo: "Estrela", grupo: "Marcos" },
  { chave: "degraus", rotulo: "Degraus", grupo: "Marcos" },
  { chave: "diamante", rotulo: "Diamante", grupo: "Marcos" },
  { chave: "dupla", rotulo: "Dupla", grupo: "Com a turma" },
  { chave: "grupo", rotulo: "Turma", grupo: "Com a turma" },
  { chave: "balao", rotulo: "Post", grupo: "Com a turma" },
  { chave: "camera", rotulo: "Foto", grupo: "Com a turma" },
  { chave: "convite", rotulo: "Indicação", grupo: "Com a turma" },
  { chave: "chave", rotulo: "Chave", grupo: "Casa e equipe" },
  { chave: "bolo", rotulo: "Aniversário", grupo: "Casa e equipe" },
  { chave: "apito", rotulo: "Apito", grupo: "Casa e equipe" },
];

export const rotuloDoIcone = (chave: string | null | undefined): string =>
  ACERVO.find((a) => a.chave === chave)?.rotulo ?? "Medalha";

// ── Estilos (§3) ───────────────────────────────────────────────────────────────────────────────────────────

export const ESTILOS: readonly { chave: EstiloMedalha; rotulo: string }[] = [
  { chave: "bronze", rotulo: "Bronze" },
  { chave: "prata", rotulo: "Prata" },
  { chave: "ouro", rotulo: "Ouro" },
  { chave: "platina", rotulo: "Platina" },
  { chave: "academia", rotulo: "Cor da academia" },
  { chave: "neon", rotulo: "Neon" },
];

export const CORES_NEON: readonly { chave: CorNeon; rotulo: string }[] = [
  { chave: "gelo", rotulo: "Gelo" },
  { chave: "verde", rotulo: "Verde" },
  { chave: "vermelho", rotulo: "Vermelho" },
];

/** "Cor da academia" = 1 dos 12 esmaltes (`--gs-esmalte-01..12`). Padrão 9 (vermelho). */
export const ESMALTE_ACADEMIA_PADRAO = 9;
export const ESMALTES = Array.from({ length: 12 }, (_, i) => i + 1);
export const LEGENDA_MEDALHA_MAX = 10;

// ── Tipos (seções do app, ordem FIXA do produto) ─────────────────────────────────────────────────────────

export const ORDEM_TIPOS: readonly TipoConquista[] = ["MARCOS", "FREQUENCIA", "TEMPO_DE_CASA", "TURMA", "EQUIPE", "PRIMEIROS_PASSOS"];

export const ROTULO_TIPO: Readonly<Record<TipoConquista, string>> = {
  MARCOS: "Marcos de treino",
  FREQUENCIA: "Frequência",
  TEMPO_DE_CASA: "Tempo de casa",
  TURMA: "Com a turma",
  EQUIPE: "Da equipe",
  PRIMEIROS_PASSOS: "Primeiros passos",
};

/** Valor do filtro `?tipo=` da lista (minúsculo, legível na URL). */
export const TIPO_NA_URL: Readonly<Record<TipoConquista, string>> = {
  MARCOS: "marcos",
  FREQUENCIA: "frequencia",
  TEMPO_DE_CASA: "tempo-de-casa",
  TURMA: "turma",
  EQUIPE: "equipe",
  PRIMEIROS_PASSOS: "primeiros-passos",
};

export function tipoDaUrl(valor: string | null): TipoConquista | null {
  if (!valor) return null;
  return (Object.keys(TIPO_NA_URL) as TipoConquista[]).find((t) => TIPO_NA_URL[t] === valor) ?? null;
}

// ── Regras por critério (espelho de REGRAS_DO_CRITERIO) ──────────────────────────────────────────────────

export interface RegraCriterio {
  tipo: TipoConquista;
  criavel: boolean;
  meta: { min: number; max: number; unidade: { um: string; varios: string } } | null;
  hora: boolean;
  repetivel: boolean;
  opcoes: boolean;
}

const sem = { hora: false, repetivel: false, opcoes: false } as const;
const un = (um: string, varios: string) => ({ um, varios });

export const REGRAS_CRITERIO: Readonly<Record<CriterioConquista, RegraCriterio>> = {
  ONBOARDING_CONCLUIDO: { tipo: "PRIMEIROS_PASSOS", criavel: false, meta: null, ...sem },
  PRIMEIRO_TREINO: { tipo: "PRIMEIROS_PASSOS", criavel: false, meta: null, ...sem },
  SEMANAS_COM_META: { tipo: "FREQUENCIA", criavel: true, meta: { min: 1, max: 52, unidade: un("semana com a meta", "semanas com a meta") }, ...sem },
  TREINOS_NO_MES: { tipo: "FREQUENCIA", criavel: true, meta: { min: 1, max: 31, unidade: un("treino", "treinos") }, ...sem },
  RETORNO_APOS_PAUSA: { tipo: "FREQUENCIA", criavel: true, meta: { min: 7, max: 90, unidade: un("dia sem vir", "dias sem vir") }, ...sem, repetivel: true },
  TREINOS_ANTES_DA_HORA: { tipo: "FREQUENCIA", criavel: true, meta: { min: 1, max: 365, unidade: un("treino", "treinos") }, ...sem, hora: true },
  TREINOS_DEPOIS_DA_HORA: { tipo: "FREQUENCIA", criavel: true, meta: { min: 1, max: 365, unidade: un("treino", "treinos") }, ...sem, hora: true },
  TREINOS_CONCLUIDOS: { tipo: "MARCOS", criavel: true, meta: { min: 10, max: 5000, unidade: un("treino", "treinos") }, ...sem },
  CHECKINS_TOTAL: { tipo: "MARCOS", criavel: true, meta: { min: 10, max: 5000, unidade: un("check-in", "check-ins") }, ...sem },
  ANOS_DE_CASA: { tipo: "TEMPO_DE_CASA", criavel: true, meta: { min: 1, max: 30, unidade: un("ano", "anos") }, ...sem },
  TREINOS_JUNTOS: { tipo: "TURMA", criavel: true, meta: { min: 1, max: 500, unidade: un("treino junto", "treinos juntos") }, ...sem, opcoes: true },
  DIAS_COM_POST: { tipo: "TURMA", criavel: true, meta: { min: 1, max: 365, unidade: un("dia com post", "dias com post") }, ...sem },
  INDICACOES_MATRICULADAS: { tipo: "TURMA", criavel: true, meta: { min: 1, max: 50, unidade: un("indicação matriculada", "indicações matriculadas") }, ...sem },
  INDICACOES_FREQUENTES: { tipo: "TURMA", criavel: true, meta: { min: 1, max: 50, unidade: un("indicado frequente", "indicados frequentes") }, ...sem },
  MANUAL: { tipo: "EQUIPE", criavel: true, meta: null, ...sem },
};

/**
 * O critério veio de um servidor mais novo que este painel (aba aberta antes de um deploy)? Quem lê `REGRAS_CRITERIO`
 * com dado do servidor passa por aqui: critério desconhecido nunca quebra a tela (QA D4).
 */
export const criterioConhecido = (criterio: string): criterio is CriterioConquista => Object.hasOwn(REGRAS_CRITERIO, criterio);

/** Regra do critério, ou null para critério desconhecido (a tela mostra um aviso em vez de quebrar). */
export const regraDoCriterio = (criterio: string): RegraCriterio | null =>
  criterioConhecido(criterio) ? REGRAS_CRITERIO[criterio] : null;

/** Tipo conhecido por este painel (as seções da lista). Desconhecido: a conquista não aparece na lista, sem quebrar. */
export const tipoConhecido = (tipo: string): tipo is TipoConquista => (ORDEM_TIPOS as readonly string[]).includes(tipo);

/** Teto das conquistas automáticas e da Cheguei (como hoje). Da equipe: o teto da academia (configuração). */
export const PONTOS_MAX_CONQUISTA = 200;
export const PONTOS_MAX_CHEGUEI = 30;
export const MOTIVO_ENTREGA_MIN = 10;
export const MOTIVO_ENTREGA_MAX = 200;
/** Motivo de alteração (auditoria): 5..200, como o resto da Gamificação. */
export const MOTIVO_ALTERACAO_MIN = 5;
export const NOME_MAX = 40;
export const DESCRICAO_MAX = 120;

/** Sufixo do campo Meta (§7): curto, como no mockup ("treinos", "indicações"). */
export const SUFIXO_META: Partial<Record<CriterioConquista, string>> = {
  SEMANAS_COM_META: "semanas",
  TREINOS_NO_MES: "treinos",
  RETORNO_APOS_PAUSA: "dias sem vir",
  TREINOS_ANTES_DA_HORA: "treinos",
  TREINOS_DEPOIS_DA_HORA: "treinos",
  TREINOS_CONCLUIDOS: "treinos",
  CHECKINS_TOTAL: "check-ins",
  ANOS_DE_CASA: "anos",
  TREINOS_JUNTOS: "treinos juntos",
  DIAS_COM_POST: "dias com post",
  INDICACOES_MATRICULADAS: "indicações",
  INDICACOES_FREQUENTES: "indicações",
};

/** Legenda padrão embaixo do número (espelho de LEGENDA_PADRAO em server/src/modulos/conquistas/medidas.ts). */
export const LEGENDA_PADRAO: Partial<Record<CriterioConquista, string>> = {
  SEMANAS_COM_META: "dias",
  TREINOS_NO_MES: "no mês",
  RETORNO_APOS_PAUSA: "de volta",
  TREINOS_ANTES_DA_HORA: "cedo",
  TREINOS_DEPOIS_DA_HORA: "noite",
  TREINOS_CONCLUIDOS: "treinos",
  CHECKINS_TOTAL: "check-ins",
  ANOS_DE_CASA: "anos",
  TREINOS_JUNTOS: "juntos",
  DIAS_COM_POST: "posts",
  INDICACOES_MATRICULADAS: "indicações",
  INDICACOES_FREQUENTES: "frequentes",
};

/**
 * Número do centro (espelho de `numeroDaMedalha` do servidor): só para a PRÉVIA ao vivo. O número gravado vem
 * sempre do servidor. `anterior` preserva o número fixo das 9 do catálogo ("15" dias) enquanto a meta não muda.
 */
export function numeroDaMedalha(
  c: { criterio: CriterioConquista; meta: number | null; hora: number | null },
  anterior?: { numero: string | null; meta: number | null } | null,
): string | null {
  if (anterior?.numero && anterior.meta === c.meta) return anterior.numero;
  switch (c.criterio) {
    case "ONBOARDING_CONCLUIDO":
    case "PRIMEIRO_TREINO":
      return "1º";
    case "TREINOS_ANTES_DA_HORA":
    case "TREINOS_DEPOIS_DA_HORA":
      return `${c.hora ?? (c.criterio === "TREINOS_ANTES_DA_HORA" ? 7 : 21)}h`;
    case "SEMANAS_COM_META":
      return c.meta === null ? null : String(c.meta * 7);
    case "MANUAL":
      return null;
    default:
      return c.meta === null ? null : String(c.meta);
  }
}

/** Critério admite número no centro (Da equipe não tem). */
export const temNumero = (criterio: CriterioConquista) => criterio !== "MANUAL";
